/**
 * Template variables: a fixed, named allow-list — never an expression.
 *
 * A template is text a company writes once and sends many times, so the
 * placeholder syntax is the one place user-authored content and customer
 * data meet. The rule that keeps that safe: a placeholder is a **lookup into
 * a fixed dictionary**, never code. `{{outstanding_amount}}` can only ever
 * mean "the value this render was given for that name" — there is no path
 * from a template's text to a function call, a property chain, or anything
 * else that would make the template itself capable of doing something its
 * author did not type in plain words.
 *
 * `renderTemplate()` makes exactly one pass over the original text. A
 * customer's own data can contain the literal characters `{{...}}` — a
 * company named `{{outstanding_amount}}`, say — and it must come out the
 * other side as that literal text, not be re-interpreted as a second
 * placeholder. A single non-recursive pass is what guarantees that; a
 * template engine that re-scanned its own output would not.
 */

export type TemplateVariable =
    | 'customer_name'
    | 'outstanding_amount'
    | 'company_name'
    | 'follow_up_date'
    | 'expected_amount'
    | 'promised_date'
    | 'contact_name';

export interface TemplateVariableDefinition {
    key: TemplateVariable;
    label: string;
    description: string;
}

export const TEMPLATE_VARIABLES: readonly TemplateVariableDefinition[] = [
    { key: 'customer_name', label: 'Customer name', description: 'The account’s own name.' },
    { key: 'outstanding_amount', label: 'Outstanding amount', description: 'What they currently owe, formatted in the company’s currency.' },
    { key: 'company_name', label: 'Your company name', description: 'This workspace’s own name.' },
    { key: 'follow_up_date', label: 'Follow-up date', description: 'The next follow-up date on the account, if one is set.' },
    { key: 'expected_amount', label: 'Expected amount', description: 'A promised or forecast amount, if one is recorded.' },
    { key: 'promised_date', label: 'Promised date', description: 'The day the customer promised to pay, while that promise is open.' },
    { key: 'contact_name', label: 'Contact name', description: 'The person at the account, if one is recorded.' },
] as const;

const KNOWN = new Set<string>(TEMPLATE_VARIABLES.map(v => v.key));

export const isTemplateVariable = (name: string): name is TemplateVariable => KNOWN.has(name);

/** What a template is rendered against. Absent fields render as empty text. */
export type TemplateContext = Partial<Record<TemplateVariable, string>>;

const TOKEN = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;

/** Every placeholder name written in the template, in the order it appears, each name once. */
export function extractVariables(content: string): string[] {
    const found: string[] = [];
    const seen = new Set<string>();
    for (const match of content.matchAll(TOKEN)) {
        const name = match[1]!;
        if (!seen.has(name)) { seen.add(name); found.push(name); }
    }
    return found;
}

export interface TemplateValidation {
    ok: boolean;
    /** Placeholder names the template uses that are not in TEMPLATE_VARIABLES. */
    unknownVariables: string[];
}

/**
 * Whether a template is safe to save. The only failure mode is an unknown
 * placeholder — free text otherwise, of any length, in any language, is
 * always valid content.
 */
export function validateTemplateContent(content: string): TemplateValidation {
    const unknown = extractVariables(content).filter(name => !isTemplateVariable(name));
    return { ok: unknown.length === 0, unknownVariables: unknown };
}

/**
 * Text-context escaping: WhatsApp and other plain-text channels have no
 * markup to break out of, so a customer-controlled value needs no character
 * transformed — only control characters (other than the newlines a message
 * legitimately spans) are stripped, since those have no honest reason to be
 * in a name or an amount and every reason to be a copy-paste accident.
 */
export const escapePlainText = (value: string): string =>
    // eslint-disable-next-line no-control-regex
    value.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, '');

/**
 * HTML-context escaping, for the email channel. The customer-controlled
 * fields — a name, a contact — are exactly the values a hostile or merely
 * unlucky source row could turn into markup or a script tag if this were
 * skipped; every one of the five reserved characters is replaced before the
 * value ever reaches the template's text.
 */
export const escapeHtml = (value: string): string =>
    value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

export type TemplateChannel = 'whatsapp' | 'email' | 'internal';

const ESCAPERS: Record<TemplateChannel, (value: string) => string> = {
    whatsapp: escapePlainText,
    internal: escapePlainText,
    email: escapeHtml,
};

/**
 * The render. One pass over `content`; every recognised placeholder is
 * replaced with its escaped value (or empty text, if this render's context
 * did not supply one) and everything else — including anything that merely
 * looks like a placeholder inside a customer's own data — is left exactly as
 * it was typed or imported.
 */
export function renderTemplate(content: string, context: TemplateContext, channel: TemplateChannel): string {
    const escape = ESCAPERS[channel];
    return content.replace(TOKEN, (whole, name: string) => {
        if (!isTemplateVariable(name)) return whole;
        const value = context[name];
        return value === undefined ? '' : escape(value);
    });
}
